import { useCallback, useEffect, useState } from 'react';

import { listMyRatings, disputeRating, type ArtistRating } from '../../lib/artist';
import { ApiError } from '../../lib/apiClient';
import { formatDate } from '../../lib/format';
import './ArtistPages.css';

/**
 * Ratings organizers have left, and the 14-day window to contest one.
 *
 * `canDispute` is computed by the engine from the same rule fileDispute
 * enforces, so the button is never offered for something the API would refuse —
 * the deadline is not re-derived here from a timestamp, which would drift.
 *
 * A rating is not removed by disputing it. An admin reviews the dispute, and
 * only a favourable resolution sets excluded_from_score and triggers a
 * recalculation. The copy says that rather than implying the score changes on
 * submission.
 */

function Mark({ ok, yes, no }: { ok: boolean | null; yes: string; no: string }) {
  if (ok === null) return <span className="art-mark art-muted"> |  not recorded</span>;
  return (
    <span className={`art-mark art-mark--${ok ? 'good' : 'bad'}`}>
      {ok ? `✓ ${yes}` : `✗ ${no}`}
    </span>
  );
}

function RatingCard({ r, onDisputed }: { r: ArtistRating; onDisputed: () => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await disputeRating(r.ratingId, reason.trim(), evidence.trim() || undefined);
      setOpen(false);
      onDisputed();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not file the dispute.');
      setBusy(false);
    }
  }

  return (
    <article className={`art-card${r.excludedFromScore ? ' art-card--muted' : ''}`}>
      <div className="art-row">
        <div className="art-row__main">
          <span className="art-row__title">{r.event.title}</span>
          <span className="art-row__meta">
            {r.event.date ? formatDate(r.event.date) : 'No date'} · rated {formatDate(r.createdAt)}
          </span>
        </div>
        <div className="art-row__side">
          {r.overallRating != null && (
            <span className="art-money mono">{r.overallRating.toFixed(1)} / 5</span>
          )}
          {r.disputed && (
            <span className={`art-badge art-badge--${r.disputeResolved ? 'signed' : 'disputed'}`}>
              {r.disputeResolved ? 'Dispute resolved' : 'Dispute under review'}
            </span>
          )}
          {r.excludedFromScore && (
            <span className="art-hint">Excluded from your score</span>
          )}
        </div>
      </div>

      <div className="art-rating__marks">
        <Mark ok={r.showedUp} yes="Showed up" no="No-show" />
        <Mark ok={r.onTime} yes="On time" no="Late" />
        {r.promotionalEffortScore != null && (
          <span className="art-mark">Promotion {r.promotionalEffortScore} / 5</span>
        )}
        {r.sellThroughAccuracy != null && (
          <span className="art-mark">Draw accuracy {Math.round(r.sellThroughAccuracy * 100)}%</span>
        )}
      </div>

      {r.notes && <p className="art-note">“{r.notes}”</p>}

      {r.disputed && r.disputeReason && (
        <p className="art-hint">Your dispute: {r.disputeReason}</p>
      )}
      {r.disputeResolved && r.disputeResolutionNotes && (
        <p className="art-hint">Admin resolution: {r.disputeResolutionNotes}</p>
      )}

      {error && <p className="art-error" role="alert">{error}</p>}

      {open ? (
        <div className="art-prompt">
          <h4 className="art-prompt__title">Contest this rating</h4>
          <div className="art-prompt__body">
            <p>
              A Voleto admin reviews the dispute. The rating keeps counting toward your
              score unless the admin rules in your favour, filing does not remove it.
            </p>
          </div>
          <label className="field">
            <span className="field__label">What was inaccurate? *</span>
            <textarea className="field__input" rows={3} value={reason} autoFocus disabled={busy}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Load-in was moved by the venue, not by us." />
          </label>
          <label className="field">
            <span className="field__label">Link to evidence (optional)</span>
            <input className="field__input mono" type="url" value={evidence} disabled={busy}
              onChange={(e) => setEvidence(e.target.value)}
              placeholder="https://…" />
          </label>
          <div className="art-prompt__actions">
            <button type="button" className="button button--ghost" disabled={busy}
              onClick={() => setOpen(false)}>Cancel</button>
            <button type="button" className="button button--primary"
              disabled={busy || !reason.trim()} onClick={submit}>
              {busy ? 'Filing…' : 'File dispute'}
            </button>
          </div>
        </div>
      ) : (
        <div className="art-actions">
          {r.canDispute && (
            <>
              <button type="button" className="button button--secondary"
                onClick={() => setOpen(true)}>
                Contest this rating
              </button>
              <span className="art-hint">
                You have until {formatDate(r.disputeDeadline)} to contest it.
              </span>
            </>
          )}
          {!r.canDispute && r.windowClosed && !r.disputed && (
            <span className="art-hint art-muted">
              The 14-day window to contest this closed on {formatDate(r.disputeDeadline)}.
            </span>
          )}
        </div>
      )}
    </article>
  );
}

export function ArtistRatings() {
  const [rows, setRows] = useState<ArtistRating[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    listMyRatings()
      .then((r) => { setRows(r); setError(null); })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load your ratings.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  if (loading) return <p className="art-status">Loading…</p>;
  if (error) return <p className="art-status art-error" role="alert">{error}</p>;

  const all = rows ?? [];

  return (
    <div className="art-page art-page--narrow">
      <header className="art-header">
        <div>
          <h1 className="art-title">Ratings</h1>
          <p className="art-lede">
            What organizers recorded after each date. These feed your reliability score.
            You have 14 days from a rating to contest it.
          </p>
        </div>
      </header>

      {all.length === 0 ? (
        <p className="art-status art-muted">No ratings yet.</p>
      ) : (
        <div className="art-list">
          {all.map((r) => <RatingCard key={r.ratingId} r={r} onDisputed={load} />)}
        </div>
      )}
    </div>
  );
}
